<template>
  <div id="app" class="immo-container">
    <!-- 页面头部 -->
    <div class="page-header">
      <h1 class="page-title">
        <span class="title-icon">🔐</span>
        防盗快捷版VIN查询
      </h1>
    </div>

    <!-- 查询卡片 -->
    <div class="query-card">
      <div class="card-header">
        <div class="header-left">
          <span class="section-title">VIN查询</span>
          <el-tag size="small" effect="plain" class="info-tag">前11位</el-tag>
        </div>
        <div class="header-right">
          <!-- VIN历史记录标签展示 -->
          <el-popover placement="bottom-end" title="VIN历史记录" :width="240" trigger="click" v-if="vinHistory.length > 0">
            <template #reference>
              <el-badge :value="vinHistory.length" :max="99" class="history-badge">
                <el-button size="small" circle class="history-btn">
                  <el-icon>
                    <Timer />
                  </el-icon>
                </el-button>
              </el-badge>
            </template>
            <div class="history-list">
              <div v-for="(vin, index) in vinHistory" :key="index" class="history-item" @click="selectHistoryVIN(vin)">
                <el-icon size="14">
                  <Timer />
                </el-icon>
                <span class="history-vin">{{ vin }}</span>
                <el-icon size="14" class="delete-history" @click.stop="removeHistoryItem(index)" title="删除">
                  <Close />
                </el-icon>
              </div>
              <div v-if="vinHistory.length === 0" class="history-empty">
                暂无历史记录
              </div>
            </div>
          </el-popover>
        </div>
      </div>

      <div class="query-content">
        <!-- 输入区域 -->
        <div class="input-wrapper">
          <el-input v-model="searchVIN" placeholder="输入 VIN 前 11 位进行查询" maxlength="11" @input="onVINInput" clearable
            class="vin-input" :prefix-icon="Search">
            <template #append>
              <el-button @click="fetchData" type="primary" :loading="loading" class="query-btn">
                查询
              </el-button>
            </template>
          </el-input>
        </div>

        <!-- 操作按钮组 -->
        <div class="action-buttons">
          <el-button size="default" type="success" :loading="btLoading2" :disabled="currentUserRole !== '开发'"
            @click="openForm" class="action-btn success-btn" :icon="Plus">
            添加
          </el-button>

          <el-tooltip :content="'上传格式正确的 .txt 文件，包含 VIN、REGION 等信息。每行数据应由 8 个字段和分隔符\\t组成'" placement="bottom">
            <el-button size="default" type="warning" :disabled="currentUserRole !== '开发' || btLoading2"
              @click="openFileInput" class="action-btn warning-btn" :icon="Upload" :loading="btLoading2">
              {{ btLoading2 ? '插入中...' : '批量插入' }}
            </el-button>
          </el-tooltip>

          <!-- 文件选择框 -->
          <input ref="fileInput" type="file" @change="handleFileChange" style="display:none;" accept=".txt" />
        </div>
      </div>

      <!-- 状态提示 -->
      <div v-if="!loading && !userRequestParameters.length" class="status-tip">
        <el-empty :description="searchVIN ? '未找到匹配的VIN数据' : '请输入VIN后查询数据'" :image-size="80">
          <template #image>
            <el-icon class="empty-icon" :size="40">
              <Search />
            </el-icon>
          </template>
        </el-empty>
      </div>
    </div>

    <!-- 数据表格卡片 -->
    <div v-if="userRequestParameters.length && !loading" class="table-card">
      <div class="table-header">
        <div class="table-title">
          <span class="title-text">查询结果</span>
          <el-tag size="small" effect="plain" class="result-count">共 {{ userRequestParameters.length }} 条</el-tag>
        </div>
        <div class="table-actions">
          <el-button size="small" @click="fetchData" :icon="Refresh" circle />
        </div>
      </div>

      <el-table :data="userRequestParameters" v-loading="loading" class="custom-table" border stripe :header-cell-style="{
        background: '#f8fafd',
        color: '#1e293b',
        fontWeight: '600',
        fontSize: '13px',
        borderBottom: '2px solid #e9eef4'
      }" :cell-style="{
          color: '#334155',
          fontSize: '13px'
        }">
        <el-table-column prop="VIN" label="VIN" width="140" show-overflow-tooltip>
          <template #default="{ row }">
            <span class="vin-cell">{{ row.VIN }}</span>
          </template>
        </el-table-column>
        <el-table-column prop="REGION" label="Region" width="90" />
        <el-table-column prop="Vehicle" label="Vehicle" width="90" />
        <el-table-column prop="Year" label="Year" width="100" />
        <el-table-column prop="YearRange" label="YearRange" width="100" show-overflow-tooltip />
        <el-table-column prop="Feature" label="Feature" width="90" show-overflow-tooltip />
        <el-table-column prop="ModeText" label="ModeText" width="100" />
        <el-table-column prop="Mode" label="Mode" width="80" />
        <el-table-column prop="CreatedAt" label="创建时间" width="160" />

        <el-table-column label="操作" width="140" fixed="right" header-align="center" align="center">
          <template #default="{ row }">
            <div class="table-actions-cell">
              <el-button size="small" type="primary" :disabled="currentUserRole !== '开发'" @click="editItem(row)"
                class="action-icon-btn" :icon="Edit" circle />
              <el-popconfirm title="确定删除此条记录吗？" @confirm="deleteItem(row.ID)">
                <template #reference>
                  <el-button size="small" type="danger" :disabled="currentUserRole !== '开发'" class="action-icon-btn"
                    :icon="Delete" circle />
                </template>
              </el-popconfirm>
            </div>
          </template>
        </el-table-column>
      </el-table>
    </div>

    <!-- 表单弹窗 -->
    <el-dialog v-model="showForm" :title="formMode === '添加' ? '添加VIN数据' : '编辑VIN数据'" width="600px" class="custom-dialog"
      destroy-on-close :close-on-click-modal="false">
      <el-form :model="form" label-width="100px" class="dialog-form" @submit.prevent="submitForm">
        <el-row :gutter="20">
          <el-col :span="12">
            <el-form-item label="VIN" required :error="errors.VIN">
              <el-input v-model="form.VIN" placeholder="11位VIN" maxlength="11" show-word-limit
                :class="{ 'is-error': errors.VIN }" />
            </el-form-item>
          </el-col>
          <el-col :span="12">
            <el-form-item label="Region" required :error="errors.REGION">
              <el-input v-model="form.REGION" placeholder="8位文本ID" maxlength="8" show-word-limit />
            </el-form-item>
          </el-col>
        </el-row>

        <el-row :gutter="20">
          <el-col :span="12">
            <el-form-item label="Vehicle" required :error="errors.Vehicle">
              <el-input v-model="form.Vehicle" placeholder="8位文本ID" maxlength="8" show-word-limit />
            </el-form-item>
          </el-col>
          <el-col :span="12">
            <el-form-item label="Year" :error="errors.Year">
              <el-input v-model="form.Year" placeholder="年份或范围" maxlength="9" />
            </el-form-item>
          </el-col>
        </el-row>

        <el-row :gutter="20">
          <el-col :span="12">
            <el-form-item label="YearRange" :error="errors.YearRange">
              <el-input v-model="form.YearRange" placeholder="8位文本ID" maxlength="8" show-word-limit />
            </el-form-item>
          </el-col>
          <el-col :span="12">
            <el-form-item label="Feature" :error="errors.Feature">
              <el-input v-model="form.Feature" placeholder="8位文本ID" maxlength="8" show-word-limit />
            </el-form-item>
          </el-col>
        </el-row>

        <el-row :gutter="20">
          <el-col :span="12">
            <el-form-item label="ModeText" required :error="errors.ModeText">
              <el-input v-model="form.ModeText" placeholder="8位文本ID" maxlength="8" show-word-limit />
            </el-form-item>
          </el-col>
          <el-col :span="12">
            <el-form-item label="Mode" required :error="errors.Mode">
              <el-input v-model="form.Mode" placeholder="4位模式号" maxlength="4" show-word-limit />
            </el-form-item>
          </el-col>
        </el-row>
      </el-form>

      <template #footer>
        <div class="dialog-footer">
          <el-button @click="closeForm">取消</el-button>
          <el-button type="primary" @click="submitForm" :loading="btLoading2">确 定</el-button>
        </div>
      </template>
    </el-dialog>
  </div>
</template>

<script>
import { addImmoMode, updateImmoMode, getImmoMode, deleteImmoMode } from '../api/users';
import { useStore } from 'vuex';
import { ElMessage, ElMessageBox } from 'element-plus';
import { Search, Plus, Upload, Refresh, Edit, Delete, Timer, Close } from '@element-plus/icons-vue';

export default {
  data() {
    return {
      userRequestParameters: [],
      loading: false,
      searchVIN: '',
      vinHistory: [],
      selectedHistoryVIN: '',
      form: {
        VIN: '',
        REGION: '',
        Vehicle: '',
        Year: '',
        YearRange: '',
        Feature: '',
        ModeText: '',
        Mode: ''
      },
      formMode: '添加',
      currentID: null,
      showForm: false,
      currentUserRole: '',
      btLoading2: false,
      errors: {}
    };
  },
  computed: {
    // 引入图标
    Search: () => Search,
    Plus: () => Plus,
    Upload: () => Upload,
    Refresh: () => Refresh,
    Edit: () => Edit,
    Delete: () => Delete,
    Timer: () => Timer,
    Close: () => Close
  },
  methods: {
    async fetchData() {
      if (this.searchVIN && this.searchVIN.length !== 11) {
        ElMessage.warning('VIN必须为11位');
        return;
      }

      // 保存到历史记录（必须是11位且不在历史中）
      if (this.searchVIN && this.searchVIN.length === 11 && !this.vinHistory.includes(this.searchVIN)) {
        this.vinHistory.unshift(this.searchVIN); // 新记录添加到开头
        // 保持最多10个历史记录
        if (this.vinHistory.length > 10) {
          this.vinHistory.pop();
        }
        // 保存到 localStorage
        localStorage.setItem('vinHistory', JSON.stringify(this.vinHistory));
      }

      this.loading = true;
      try {
        if (!this.searchVIN) {
          this.userRequestParameters = [];
          return;
        }

        const response = await getImmoMode({ vin: this.searchVIN.slice(0, 11) });
        this.userRequestParameters = response.data.tableData || [];

        if (this.userRequestParameters.length === 0) {
          ElMessage.info('未找到匹配的VIN数据');
        } else {
          ElMessage.success('查询成功');
        }
      } catch (error) {
        console.error(error);
        ElMessage.error('加载数据失败');
      } finally {
        this.loading = false;
      }
    },

    async deleteItem(id) {
      try {
        await deleteImmoMode(id);
        ElMessage.success('删除成功');
        this.fetchData();
      } catch (error) {
        ElMessage.error('删除失败');
        console.error(error);
      }
    },

    editItem(item) {
      this.formMode = '修改';
      this.form = { ...item };
      this.currentID = item.ID;
      this.showForm = true;
    },

    onVINInput() {
      this.searchVIN = event.target.value.toUpperCase();
    },

    // 选择历史VIN
    selectHistoryVIN(vin) {
      this.searchVIN = vin;
      this.fetchData();
    },

    // 删除历史记录
    removeHistoryItem(index) {
      this.vinHistory.splice(index, 1);
      localStorage.setItem('vinHistory', JSON.stringify(this.vinHistory));
      ElMessage.success('已从历史记录中删除');
    },

    // 清空所有历史记录
    clearAllHistory() {
      ElMessageBox.confirm('确定清空所有历史记录吗？', '提示', {
        confirmButtonText: '确定',
        cancelButtonText: '取消',
        type: 'warning'
      }).then(() => {
        this.vinHistory = [];
        localStorage.removeItem('vinHistory');
        ElMessage.success('历史记录已清空');
      }).catch(() => { });
    },

loadVINHistory() {
  const savedHistory = localStorage.getItem('vinHistory');
  if (savedHistory) {
    try {
      const parsed = JSON.parse(savedHistory);
      // 过滤掉无效的VIN（空字符串、长度不是11位等）
      this.vinHistory = parsed.filter(vin => 
        vin && typeof vin === 'string' && vin.trim() !== '' && vin.length === 11
      );
      
      // 如果过滤后数据变了，更新localStorage
      if (this.vinHistory.length !== parsed.length) {
        localStorage.setItem('vinHistory', JSON.stringify(this.vinHistory));
      }
    } catch (e) {
      console.error('解析历史记录失败', e);
      this.vinHistory = [];
      localStorage.removeItem('vinHistory');
    }
  }
},
    validateForm() {
      this.form.VIN = this.form.VIN?.toUpperCase() || '';
      this.form.REGION = this.form.REGION?.toUpperCase() || '';
      this.form.Vehicle = this.form.Vehicle?.toUpperCase() || '';
      this.form.Year = this.form.Year?.toUpperCase() || '';
      this.form.YearRange = this.form.YearRange?.toUpperCase() || '';
      this.form.Feature = this.form.Feature?.toUpperCase() || '';
      this.form.ModeText = this.form.ModeText?.toUpperCase() || '';
      this.form.Mode = this.form.Mode?.toUpperCase() || '';

      this.errors = {};
      const regex = {
        VIN: /^[A-HJ-NPR-Z0-9?]{11}$/,
        REGION: /^[0-9A-F]{8}$/,
        Vehicle: /^[0-9A-F]{8}$/,
        Year: /^(\d{4}|-\d{4}|\d{4}-\d{4}|\d{4}-$|^)$/,
        YearRange: /^[0-9A-F]{8}$/,
        Feature: /^[0-9A-F]{8}$/,
        ModeText: /^[0-9A-F]{8}$/,
        Mode: /^[0-9A-F]{4}$/
      };

      if (!this.form.VIN || !regex.VIN.test(this.form.VIN)) {
        this.errors.VIN = 'VIN必须是11位的合法字符串';
      }
      if (!this.form.REGION || !regex.REGION.test(this.form.REGION)) {
        this.errors.REGION = 'Region必须是8位的数字或字母（0-9, A-F）';
      }
      if (!this.form.Vehicle || !regex.Vehicle.test(this.form.Vehicle)) {
        this.errors.Vehicle = 'Vehicle必须是8位的数字或字母（0-9, A-F）';
      }
      if (this.form.Year !== "NULL" && this.form.Year !== "" && !regex.Year.test(this.form.Year)) {
        this.errors.Year = 'Year格式不正确';
      }
      if (this.form.YearRange && !regex.YearRange.test(this.form.YearRange)) {
        this.errors.YearRange = 'YearRange必须是8位的数字或字母（0-9, A-F）';
      }
      if (this.form.Feature !== "NULL" && this.form.Feature !== "" && !regex.Feature.test(this.form.Feature)) {
        this.errors.Feature = 'Feature必须是8位的数字或字母（0-9, A-F）';
      }
      if (!this.form.ModeText || !regex.ModeText.test(this.form.ModeText)) {
        this.errors.ModeText = 'ModeText必须是8位的数字或字母（0-9, A-F）';
      }
      if (!this.form.Mode || !regex.Mode.test(this.form.Mode)) {
        this.errors.Mode = 'Mode必须是4位的数字或字母（0-9, A-F）';
      }

      return Object.keys(this.errors).length === 0;
    },

    async submitForm() {
      if (!this.validateForm()) {
        ElMessage.error('表单验证失败，请检查输入');
        return;
      }

      this.btLoading2 = true;
      try {
        if (!this.form.Year || this.form.Year === "NULL") {
          this.form.Year = "NULL";
          this.form.YearRange = "AAAA00F6";
        }
        if (!this.form.Feature) {
          this.form.Feature = "NULL";
        }

        if (this.formMode === '添加') {
          await addImmoMode(this.form);
          ElMessage.success('添加成功');
        } else {
          await updateImmoMode(this.form);
          ElMessage.success('修改成功');
        }

        this.fetchData();
        this.closeForm();
      } catch (error) {
        ElMessage.error(`${this.formMode}失败`);
        console.error('Error during submission:', error);
      } finally {
        this.btLoading2 = false;
      }
    },

    openForm() {
      this.formMode = '添加';
      this.form = {
        VIN: '',
        REGION: '',
        Vehicle: '',
        Year: '',
        YearRange: '',
        Feature: '',
        ModeText: '',
        Mode: ''
      };
      this.errors = {};
      this.showForm = true;
    },

    closeForm() {
      this.showForm = false;
      this.errors = {};
    },

    openFileInput() {
      if (this.btLoading2) return;
      this.$refs.fileInput.value = null;
      this.$nextTick(() => {
        this.$refs.fileInput.click();
      });
    },

    async handleFileChange(event) {
      this.btLoading2 = true;
      const file = event.target.files[0];
      if (!file) {
        this.btLoading2 = false;
        ElMessage.warning('请选择一个文件');
        return;
      }

      const reader = new FileReader();
      reader.onload = async (e) => {
        const fileContent = e.target.result;
        const records = this.parseFileContent(fileContent);

        if (records.length === 0) {
          this.btLoading2 = false;
          ElMessage.warning('文件内容无效，请检查文件格式');
          return;
        }

        try {
          await this.batchInsertData(records);
        } catch (error) {
          console.error('批量插入失败:', error);
        } finally {
          this.btLoading2 = false;
        }
      };
      reader.onerror = () => {
        this.btLoading2 = false;
        ElMessage.error('文件读取失败');
      };
      reader.readAsText(file);
    },

    parseFileContent(content) {
      const lines = content.trim().split('\n');
      const records = [];
      const regex = {
        VIN: /^[A-HJ-NPR-Z0-9?]{11}$/,
        REGION: /^[0-9A-F]{8}$/,
        Vehicle: /^[0-9A-F]{8}$/,
        Year: /^(\d{4}|-\d{4}|\d{4}-\d{4}|\d{4}-$|^)$/,
        YearRange: /^[0-9A-F]{8}$/,
        Feature: /^[0-9A-F]{8}$/,
        ModeText: /^[0-9A-F]{8}$/,
        Mode: /^[0-9A-F]{4}$/
      };

      let errorCount = 0;

      for (let lineIndex = 0; lineIndex < lines.length; lineIndex++) {
        const line = lines[lineIndex];
        const fields = line.split('\t').map(field => field.trim());

        if (fields.length === 8) {
          const [VIN, REGION, Vehicle, Year, YearRange, Feature, ModeText, Mode] = fields;

          const record = {
            VIN,
            REGION,
            Vehicle,
            YearRange,
            Year,
            Feature,
            ModeText,
            Mode
          };

          if (!record.Year || record.Year === "NULL") {
            record.Year = "NULL";
            record.YearRange = "AAAA00F6";
          }
          if (!record.Feature) {
            record.Feature = "NULL";
          }

          // 转换为大写
          Object.keys(record).forEach(key => {
            if (record[key]) record[key] = record[key].toUpperCase();
          });

          const lineErrors = [];

          if (!record.VIN || !regex.VIN.test(record.VIN)) {
            lineErrors.push(`VIN格式错误`);
          }
          if (!record.REGION || !regex.REGION.test(record.REGION)) {
            lineErrors.push(`Region格式错误`);
          }
          if (!record.Vehicle || !regex.Vehicle.test(record.Vehicle)) {
            lineErrors.push(`Vehicle格式错误`);
          }
          if (record.Year !== "NULL" && record.Year !== "" && !regex.Year.test(record.Year)) {
            lineErrors.push(`Year格式错误`);
          }
          if (record.YearRange && !regex.YearRange.test(record.YearRange)) {
            lineErrors.push(`YearRange格式错误`);
          }
          if (record.Feature !== "NULL" && record.Feature !== "" && !regex.Feature.test(record.Feature)) {
            lineErrors.push(`Feature格式错误`);
          }
          if (!record.ModeText || !regex.ModeText.test(record.ModeText)) {
            lineErrors.push(`ModeText格式错误`);
          }
          if (!record.Mode || !regex.Mode.test(record.Mode)) {
            lineErrors.push(`Mode格式错误`);
          }

          if (lineErrors.length > 0) {
            errorCount++;
            ElMessage.error(`第 ${lineIndex + 1} 行数据格式错误：${lineErrors.join('、')}`);
          } else {
            records.push(record);
            errorCount = 0;
          }

          if (errorCount >= 10) {
            ElMessage.error('连续10条数据出错，操作已终止');
            return [];
          }
        } else {
          ElMessage.error(`第 ${lineIndex + 1} 行字段数不正确`);
          return [];
        }
      }

      return records;
    },

    async batchInsertData(records) {
      this.btLoading2 = true;
      let successCount = 0;
      let failureCount = 0;

      try {
        await addImmoMode(records);
        successCount = records.length;
        ElMessage.success(`${successCount}条记录插入成功`);
        this.fetchData();
      } catch (error) {
        failureCount = records.length;
        ElMessage.error(`插入失败：${error.message || '未知错误'}`);
        console.error('Failed to insert records:', error);
      } finally {
        this.btLoading2 = false;
      }
    }
  },

  mounted() {
    this.loadVINHistory();
    this.fetchData();
    const store = useStore();
    this.currentUserRole = store.state.userData?.job;
  },
};
</script>

<style scoped>
/* 全局容器 */
.immo-container {
  padding: 24px 32px;
  background: #f5f7fb;
  min-height: 100vh;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif;
}

/* 页面头部 */
.page-header {
  margin-bottom: 24px;
}

.page-title {
  display: flex;
  align-items: center;
  gap: 12px;
  font-size: 24px;
  font-weight: 600;
  color: #0f172a;
  margin: 0;
  letter-spacing: 0.3px;
}

.title-icon {
  font-size: 28px;
}

/* 查询卡片 */
.query-card {
  background: white;
  border-radius: 20px;
  padding: 24px;
  margin-bottom: 24px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.02);
  border: 1px solid #edf2f7;
}

.card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
}

.header-left {
  display: flex;
  align-items: center;
  gap: 12px;
}

.section-title {
  font-size: 16px;
  font-weight: 600;
  color: #1e293b;
}

.info-tag {
  background: #eef2ff;
  color: #4f46e5;
  border: none;
  border-radius: 30px;
  padding: 0 10px;
}

.header-right {
  display: flex;
  align-items: center;
}

/* 历史记录按钮 */
.history-btn {
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  color: #64748b;
  transition: all 0.2s ease;
}

.history-btn:hover {
  background: #eef2ff;
  border-color: #4f46e5;
  color: #4f46e5;
  transform: translateY(-1px);
  box-shadow: 0 4px 10px rgba(79, 70, 229, 0.1);
}

.history-badge :deep(.el-badge__content) {
  background: #4f46e5;
  border: 2px solid white;
  font-size: 10px;
  height: 16px;
  line-height: 16px;
}

/* 历史记录列表 */
.history-list {
  max-height: 300px;
  overflow-y: auto;
  padding: 4px 0;
}

.history-item {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 12px;
  cursor: pointer;
  border-radius: 8px;
  transition: all 0.2s;
  color: #334155;
  position: relative;
}

.history-item:hover {
  background: #eef2ff;
  color: #4f46e5;
}

.history-item:hover .delete-history {
  opacity: 1;
}

.history-vin {
  flex: 1;
  font-family: 'SF Mono', Monaco, 'Cascadia Code', 'Roboto Mono', monospace;
  font-size: 13px;
}

.delete-history {
  opacity: 0;
  color: #94a3b8;
  transition: all 0.2s;
  padding: 4px;
  border-radius: 50%;
}

.delete-history:hover {
  color: #ef4444;
  background: #fef2f2;
}

.history-empty {
  padding: 20px;
  text-align: center;
  color: #94a3b8;
  font-size: 13px;
}

/* 查询内容区域 */
.query-content {
  display: flex;
  gap: 16px;
  align-items: center;
  flex-wrap: wrap;
}

.input-wrapper {
  flex: 0 0 auto; /* 取消自动拉伸 */
  width: 380px; /* 设置固定宽度，根据需要调整这个值 */
  min-width: 300px;
}

.vin-input {
  width: 100%;
}

.vin-input :deep(.el-input__wrapper) {
  border-radius: 40px 0 0 40px;
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  box-shadow: none;
  padding-right: 35px; /* 为清除按钮留出空间 */
}

.vin-input :deep(.el-input__wrapper:hover) {
  border-color: #4f46e5;
  background: white;
}

.vin-input :deep(.el-input__suffix) {
  right: 8px;
}

.vin-input :deep(.el-input-group__append) {
  background: transparent;
  border: none;
  box-shadow: none !important;
  padding: 0;
}

.query-btn {
  border-radius: 0 40px 40px 0 !important;
  height: 40px;
  padding: 0 20px;
  background: linear-gradient(145deg, #4f46e5, #6366f1);
  border: none;
  font-weight: 500;
  min-width: 70px;
  outline: none !important;
  box-shadow: none !important;
}

.query-btn:focus,
.query-btn:focus-visible {
  outline: none !important;
  box-shadow: none !important;
}

.vin-input :deep(.el-input__wrapper:focus-within) {
  box-shadow: 0 0 0 1px #4f46e5 inset !important;
}


/* 操作按钮组 */
.action-buttons {
  display: flex;
  gap: 12px;
  flex-shrink: 0;
  margin-left: auto; /* 添加这一行，让按钮组靠右对齐 */
}

.action-btn {
  border-radius: 40px !important;
  padding: 0 20px !important;
  height: 40px;
  font-weight: 500;
  border: none;
  transition: all 0.2s ease;
}

.action-btn.success-btn {
  background: linear-gradient(145deg, #10b981, #14b8a6);
  color: white;
}

.action-btn.success-btn:hover {
  background: linear-gradient(145deg, #14b8a6, #10b981);
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(16, 185, 129, 0.25);
}

.action-btn.warning-btn {
  background: linear-gradient(145deg, #f59e0b, #f97316);
  color: white;
}

.action-btn.warning-btn:hover {
  background: linear-gradient(145deg, #f97316, #f59e0b);
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(245, 158, 11, 0.25);
}

/* 状态提示 */
.status-tip {
  margin-top: 24px;
  padding: 32px 0;
  background: #f8fafc;
  border-radius: 16px;
}

.empty-icon {
  color: #94a3b8;
}

/* 表格卡片 */
.table-card {
  background: white;
  border-radius: 20px;
  padding: 20px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.02);
  border: 1px solid #edf2f7;
}

.table-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
}

.table-title {
  display: flex;
  align-items: center;
  gap: 12px;
}

.title-text {
  font-size: 16px;
  font-weight: 600;
  color: #1e293b;
}

.result-count {
  background: #eef2ff;
  color: #4f46e5;
  border: none;
  border-radius: 30px;
}

/* 自定义表格 */
.custom-table {
  width: 100%;
  border-radius: 16px;
  overflow: hidden;
  border: 1px solid #e9eef4;
}

.custom-table :deep(.el-table__row) {
  transition: background 0.15s;
}

.custom-table :deep(.el-table__row:hover) {
  background: #f8fafc !important;
}

.vin-cell {
  font-family: 'SF Mono', Monaco, 'Cascadia Code', 'Roboto Mono', monospace;
  font-weight: 500;
  color: #0f172a;
}

/* 表格操作单元格 */
.table-actions-cell {
  display: flex;
  gap: 8px;
  justify-content: center;
  align-items: center;
}

.action-icon-btn {
  border: none !important;
  transition: all 0.2s ease;
}

.action-icon-btn.el-button--primary {
  background: #eef2ff;
  color: #4f46e5;
}

.action-icon-btn.el-button--primary:hover {
  background: #4f46e5;
  color: white;
  transform: translateY(-2px);
  box-shadow: 0 4px 10px rgba(79, 70, 229, 0.2);
}

.action-icon-btn.el-button--danger {
  background: #fef2f2;
  color: #ef4444;
}

.action-icon-btn.el-button--danger:hover {
  background: #ef4444;
  color: white;
  transform: translateY(-2px);
  box-shadow: 0 4px 10px rgba(239, 68, 68, 0.2);
}

/* 自定义弹窗 */
.custom-dialog :deep(.el-dialog) {
  border-radius: 24px;
  overflow: hidden;
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.08);
}

.custom-dialog :deep(.el-dialog__header) {
  background: #f8fafc;
  padding: 20px 24px;
  margin: 0;
  border-bottom: 1px solid #e9eef4;
}

.custom-dialog :deep(.el-dialog__title) {
  color: #0f172a;
  font-weight: 600;
  font-size: 18px;
}

.custom-dialog :deep(.el-dialog__body) {
  padding: 28px 24px;
}

.custom-dialog :deep(.el-dialog__footer) {
  padding: 16px 24px 24px;
  border-top: 1px solid #e9eef4;
  background: #f8fafc;
}

/* 弹窗表单 */
.dialog-form :deep(.el-form-item__label) {
  color: #475569;
  font-weight: 500;
  font-size: 13px;
}

.dialog-form :deep(.el-input__wrapper) {
  border-radius: 12px;
  border: 1px solid #e2e8f0;
  box-shadow: none;
  padding: 4px 12px;
}

.dialog-form :deep(.el-input__wrapper:hover) {
  border-color: #4f46e5;
}

.dialog-form :deep(.el-input__inner) {
  height: 36px;
  line-height: 36px;
  font-size: 13px;
}

/* 表单错误样式 */
.dialog-form :deep(.is-error .el-input__wrapper) {
  border-color: #ef4444;
}

/* 弹窗底部按钮 */
.dialog-footer {
  display: flex;
  justify-content: flex-end;
  gap: 12px;
}

.dialog-footer .el-button {
  border-radius: 40px;
  padding: 0 24px;
  height: 36px;
  font-weight: 500;
  transition: all 0.2s ease;
}

.dialog-footer .el-button--primary {
  background: linear-gradient(145deg, #4f46e5, #6366f1);
  border: none;
  color: white;
}

.dialog-footer .el-button--primary:hover {
  background: linear-gradient(145deg, #6366f1, #4f46e5);
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(79, 70, 229, 0.25);
}

/* 滚动条美化 */
::-webkit-scrollbar {
  width: 6px;
  height: 6px;
}

::-webkit-scrollbar-thumb {
  background: #cbd5e1;
  border-radius: 3px;
}

::-webkit-scrollbar-track {
  background: #f1f5f9;
}

/* 响应式 */
@media screen and (max-width: 768px) {
  .immo-container {
    padding: 16px;
  }

  .query-content {
    flex-direction: column;
  }

.input-wrapper {
  flex: 0 0 auto; /* 取消自动拉伸 */
  width: 380px; /* 设置固定宽度，根据需要调整这个值 */
  min-width: 300px;
}

  .action-buttons {
    width: 100%;
    justify-content: stretch;
  }

  .action-btn {
    flex: 1;
  }
}
</style>